import React from 'react';
import BottomSheet from './ui/BottomSheet';
import { useDevice } from '../hooks/useDevice';
import { SignedIn, SignedOut, UserButton, SignInButton, SignUpButton, useUser, useClerk } from '@clerk/clerk-react';
import ResidenceTab from './UserProfileTabs/ResidenceTab';
import PaymentsTab from './UserProfileTabs/PaymentsTab';
// CB:BEGIN fr-001-import-settings-tabs
import CoreBidsNotificationsTab from './UserProfileTabs/CoreBidsNotificationsTab';
import CoreBidsPrivacyTab from './UserProfileTabs/CoreBidsPrivacyTab';
import CoreBidsAdminSettingsTab from './UserProfileTabs/CoreBidsAdminSettingsTab';
// CB:END fr-001-import-settings-tabs
import { isDemoMode } from '../lib/featureFlags';

interface SharedHeaderAdminProps {
  onNavigate: (screen: string) => void;
  homeownerName?: string;
  contractorName?: string;
}

const SharedHeaderAdmin: React.FC<SharedHeaderAdminProps> = ({ onNavigate, homeownerName, contractorName }) => {
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const device = useDevice();
  // CB:BEGIN fr-001-userbutton-ref
  const userButtonContainerRef = React.useRef<HTMLDivElement>(null);
  
  // Function to open user profile modal directly (skip dropdown)
  const triggerUserButton = React.useCallback(() => {
    const manageAccountLink = document.querySelector('a[href*="account"], button[aria-label*="Manage account"], [data-clerk-element="manageAccount"]') as HTMLElement;
    if (manageAccountLink) {
      manageAccountLink.click();
      return;
    }
    
    requestAnimationFrame(() => {
      let clicked = false;
      
      if (userButtonContainerRef.current) {
        const buttons = userButtonContainerRef.current.querySelectorAll('button');
        for (const button of Array.from(buttons)) {
          if (button.disabled) continue;
          button.click();
          clicked = true;
          break;
        }
      }
      
      if (!clicked) {
        const container = document.querySelector('[data-user-button-container="true"]');
        if (container) {
          const button = container.querySelector('button') as HTMLButtonElement;
          if (button) {
            button.click();
            clicked = true;
          }
        }
      }
      
      if (clicked) {
        setTimeout(() => {
          const selectors = [
            'a[href*="account"]',
            'button[aria-label*="Manage account"]',
            '[data-clerk-element="manageAccount"]',
          ];
          
          for (const selector of selectors) {
            try {
              const link = document.querySelector(selector) as HTMLElement;
              if (link && link.offsetParent !== null) {
                link.click();
                return;
              }
            } catch (e) {}
          }
          
          const allLinks = document.querySelectorAll('a, button');
          for (const link of Array.from(allLinks)) {
            const text = link.textContent?.toLowerCase() || '';
            if ((text.includes('manage') || text.includes('account')) && link.offsetParent !== null) {
              (link as HTMLElement).click();
              return;
            }
          }
        }, 100);
      } else {
        openUserProfile();
      }
    });
  }, [openUserProfile]);
  // CB:END fr-001-userbutton-ref

  const handleSignOut = async () => {
    try {
      await signOut();
      onNavigate('landing');
    } catch (error) {
      console.error('Error signing out:', error);
      onNavigate('landing');
    }
  };

  const btnColors = {
    blue:   { base: '#6b7280', hover: '#4b5563' },
    indigo: { base: '#6366f1', hover: '#4f46e5' },
    green:  { base: '#059669', hover: '#047857' },
  };
  const solidBtn = (c: keyof typeof btnColors) => ({
    style: {
      fontSize: 14, padding: '7px 14px', color: 'white' as const,
      background: btnColors[c].base, border: 'none', fontWeight: 500,
      borderRadius: 6, cursor: 'pointer' as const, transition: 'background 0.15s',
      whiteSpace: 'nowrap' as const,
    },
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = btnColors[c].hover; },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = btnColors[c].base; },
  });

  // Create the admin navigation buttons
  const adminNavButtons = (
    <>
      <button {...solidBtn('blue')} onClick={() => onNavigate('admin-dashboard')}>
        Home
      </button>
      <button {...solidBtn('blue')} onClick={() => onNavigate('admin-homeowners-list')}>
        Homeowners
      </button>
      <button {...solidBtn('blue')} onClick={() => onNavigate('admin-contractors-list')}>
        Contractors
      </button>
      <button {...solidBtn('blue')} onClick={() => onNavigate('admin-projects-list')}>
        Projects
      </button>
      {/* CB:BEGIN operations-nav-button */}
      <button {...solidBtn('blue')} onClick={() => onNavigate('admin-operations')}>
        Operations
      </button>
      {/* CB:END operations-nav-button */}
      {/* CB:BEGIN create-invoice-button */}
      <button {...solidBtn('green')} onClick={() => onNavigate('admin-invoices-create')}>
        Create Invoice
      </button>
      {/* CB:END create-invoice-button */}
      {/* CB:BEGIN admin-reviews-nav */}
      <button {...solidBtn('indigo')} onClick={() => onNavigate('admin-reviews')}>
        Reviews
      </button>
      {/* CB:END admin-reviews-nav */}
      {/* CB:BEGIN admin-help-nav */}
      <button {...solidBtn('blue')} onClick={() => onNavigate('admin-help')}>
        Help
      </button>
      {/* CB:END admin-help-nav */}

      {/* CB:BEGIN settings-button */}
      <button {...solidBtn('blue')} onClick={triggerUserButton}>
        Settings
      </button>
      {/* CB:END settings-button */}
      
      {/* Name Display */}
      {(homeownerName || contractorName) && (
        <span style={{ 
          color: '#3b82f6', 
          fontSize: 16, 
          fontWeight: 600,
          marginLeft: 16
        }}>
          {homeownerName || contractorName}
        </span>
      )}
    </>
  );

  const adminBadge = (
    <span style={{
      padding: '4px 10px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      color: '#7c3aed',
      background: '#ede9fe',
      border: '1px solid #c4b5fd'
    }}>
      Admin
    </span>
  );

  const [menuOpen, setMenuOpen] = React.useState(false);
  const { isMobileLayout } = useDevice();

  return (
    <div className="header sticky top-0 z-40" style={{ background: '#123c3b', color: 'white', padding: '20px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {/* CB:BEGIN safe-area-top-spacer */}
      <div className="sr-only" style={{ paddingTop: 'env(safe-area-inset-top)' }} />
      {/* CB:END safe-area-top-spacer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
        <div className="logo" style={{ fontSize: 24, fontWeight: 600, letterSpacing: -0.5, cursor: 'pointer' }} onClick={() => onNavigate('landing')}>CoreBids</div>
        
        
        {/* Admin Navigation Buttons - only shown when logged in */}
        <SignedIn>
          {!isMobileLayout && (
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              {adminNavButtons}
            </div>
          )}
        </SignedIn>
      </div>
      
      {!isMobileLayout && (
      <div className="nav-buttons" style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
        <SignedIn>
          <div style={{ marginRight: 8 }}>
            {adminBadge}
          </div>
          <span style={{ color: 'rgba(255,255,255,0.8)' }}>Welcome, {user?.firstName || user?.fullName?.split(' ')[0] || 'Admin'}</span>
          {/* CB:BEGIN fr-001-userbutton-ref-wrapper */}
          <div ref={userButtonContainerRef} data-user-button-container="true" style={{ display: 'inline-block' }}>
          {/* CB:END fr-001-userbutton-ref-wrapper */}
          <UserButton afterSignOutUrl="/" userProfileMode="modal">
            <UserButton.UserProfilePage label="Residence" url="residence" labelIcon={<span>🏠</span>}>
              <ResidenceTab />
            </UserButton.UserProfilePage>
            <UserButton.UserProfilePage label="Payments" url="payments" labelIcon={<span>💳</span>}>
              <PaymentsTab />
            </UserButton.UserProfilePage>
            {/* CB:BEGIN fr-001-corebids-settings-pages */}
            <UserButton.UserProfilePage label="CoreBids Notifications" url="corebids-notifications" labelIcon={<span>🔔</span>}>
              <CoreBidsNotificationsTab />
            </UserButton.UserProfilePage>
            <UserButton.UserProfilePage label="CoreBids Privacy" url="corebids-privacy" labelIcon={<span>🔒</span>}>
              <CoreBidsPrivacyTab />
            </UserButton.UserProfilePage>
            <UserButton.UserProfilePage label="CoreBids Admin Settings" url="corebids-admin-settings" labelIcon={<span>⚙️</span>}>
              <CoreBidsAdminSettingsTab />
            </UserButton.UserProfilePage>
            {/* CB:END fr-001-corebids-settings-pages */}
          </UserButton>
          {/* CB:BEGIN fr-001-userbutton-ref-wrapper-close */}
          </div>
          {/* CB:END fr-001-userbutton-ref-wrapper-close */}
        </SignedIn>
        {/* CB:BEGIN header-mobile-sizes */}
        <SignedOut>
          <SignInButton mode="modal">
            <button className="btn btn-secondary" style={{ color: 'black', fontSize: device.isMobileLayout ? '12px' : '14px', padding: device.isMobileLayout ? '4px 10px' : '8px 16px' }}>Login</button>
          </SignInButton>
          <SignUpButton mode="modal">
            <button className="btn btn-primary" style={{ color: 'black', fontSize: device.isMobileLayout ? '12px' : '14px', padding: device.isMobileLayout ? '4px 10px' : '8px 16px' }}>Sign Up</button>
          </SignUpButton>
        </SignedOut>
        {/* CB:END header-mobile-sizes */}
      </div>
      )}
      {/* Mobile hamburger - removed */}

      {isMobileLayout && (
      <BottomSheet isOpen={menuOpen} onClose={() => setMenuOpen(false)} title="Menu">
        <div style={{ display: 'grid', gap: 12 }}>
          <SignedIn>
            <div style={{ display: 'grid', gap: 8 }}>
              {adminNavButtons}
              <div>{adminBadge}</div>
            </div>
          </SignedIn>
          {/* CB:BEGIN header-mobile-sizes-2 */}
          <SignedOut>
            <SignInButton mode="modal">
              <button className="btn btn-secondary" style={{ color: 'black', fontSize: device.isMobileLayout ? '12px' : '14px', padding: device.isMobileLayout ? '4px 10px' : '8px 16px' }}>Login</button>
            </SignInButton>
            <SignUpButton mode="modal">
              <button className="btn btn-primary" style={{ color: 'black', fontSize: device.isMobileLayout ? '12px' : '14px', padding: device.isMobileLayout ? '4px 10px' : '8px 16px' }}>Sign Up</button>
            </SignUpButton>
          </SignedOut>
          {/* CB:END header-mobile-sizes-2 */}
        </div>
      </BottomSheet>
      )}
    </div>
  );
};

export default SharedHeaderAdmin;
